/**
 * m1-chase-and-send TG-4 (ADR-20261021-wzgk): the client for the owner's server-side section
 * confirmation, `POST /api/portal/sections/{key}/confirmation`
 * (`src/api/contracts/section-confirmation.openapi.yaml`).
 *
 * Section confirmation is otherwise computed in the browser only (`routing/section-state.ts`), so
 * nothing the chase could trust existed. The chase picks the not-started reminder or the
 * partially-complete reminder from this record. The call carries no renewal identifier and no body:
 * the server takes the renewal from the validated session claim and refuses any role but the owner.
 *
 * Best effort by design. The working-copy write is the confirmation itself; this call only tells
 * the chase about it, so it never throws, never blocks the customer and reports its outcome as a
 * value the caller may ignore. The server is idempotent per renewal and section, so a repeated
 * call is harmless.
 */
import { authenticatedFetch } from "./authenticated-fetch";

export type SectionConfirmationOutcome = "recorded" | "not_permitted" | "error";

/** Records that the renewal's owner confirmed `sectionKey` (a portal section id, never an
 * instance id). Resolves, never rejects. */
export async function recordSectionConfirmation(sectionKey: string): Promise<SectionConfirmationOutcome> {
  let response: Response;
  try {
    response = await authenticatedFetch(`/api/portal/sections/${encodeURIComponent(sectionKey)}/confirmation`, {
      method: "POST",
    });
  } catch {
    return "error";
  }

  if (response.status === 200) {
    return "recorded";
  }
  if (response.status === 403) {
    return "not_permitted";
  }
  return "error";
}
